import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, RefreshCw } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line, CartesianGrid,
} from "recharts";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";

const PURPLE = "#5b2ea6";
const fmtMs = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms || 0}ms`);
const fmtSec = (s) => (s >= 60 ? `${Math.round(s / 60)}m` : `${s || 0}s`);

function Stat({ label, value, danger }) {
  return (
    <div className="bg-slate-50 rounded-xl p-4">
      <div className={`text-2xl font-extrabold ${danger ? "text-red-600" : "text-slate-900"}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-4">
      <h2 className="text-base font-extrabold text-slate-900 mb-4">{title}</h2>
      {children}
    </div>
  );
}

export default function AdminPortal() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const phone = user?.phone || "9873117605";

  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [d, setD] = useState({});

  const load = useCallback(async () => {
    setError(null);
    try {
      const params = { phone, days };
      const [ov, fn, sc, sv, pf] = await Promise.all([
        api.get("/analytics/overview", { params }),
        api.get("/analytics/funnel", { params }),
        api.get("/analytics/screens", { params }),
        api.get("/analytics/services", { params }),
        api.get("/analytics/performance", { params }),
      ]);
      setD({ ov: ov.data, fn: fn.data, sc: sc.data, sv: sv.data, pf: pf.data });
    } catch (e) {
      setError(e?.response?.status === 403 ? "Admin access only." : "Could not load analytics.");
    } finally {
      setLoading(false);
    }
  }, [days, phone]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const ov = d.ov || {};
  const pf = d.pf || {};
  const funnelData = (d.fn?.stages || []).map((s) => ({ name: s.label, entered: s.entered, drop: s.drop_pct, med: s.median_time_to_complete_ms }));
  const svcData = (d.sv?.services || []).slice(0, 12);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-[#5b2ea6] text-white px-4 py-4 flex items-center justify-between sticky top-0 z-10">
        <button onClick={() => navigate("/admin-choice")} data-testid="admin-back" className="p-1"><ChevronLeft /></button>
        <h1 className="text-lg font-extrabold">Admin Portal</h1>
        <button onClick={load} data-testid="admin-refresh" className="p-1"><RefreshCw size={20} /></button>
      </div>

      <div className="bg-[#5b2ea6] px-4 pb-4 flex gap-2">
        {[7, 30, 90].map((x) => (
          <button key={x} data-testid={`period-${x}`} onClick={() => setDays(x)}
            className={`px-4 py-1.5 rounded-full text-sm font-bold ${days === x ? "bg-white text-[#5b2ea6]" : "bg-white/20 text-white"}`}>
            {x}d
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-16 text-center text-slate-500">Loading analytics…</div>
      ) : error ? (
        <div className="p-16 text-center text-red-600">{error}</div>
      ) : (
        <div className="max-w-5xl mx-auto p-4">
          <Card title="Overview">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Installs (period)" value={ov.installs ?? 0} />
              <Stat label="Installs (all-time)" value={ov.installs_all_time ?? 0} />
              <Stat label="New signups" value={ov.signups_complete ?? 0} />
              <Stat label="Incomplete signups" value={ov.incomplete_signups ?? 0} danger={ov.incomplete_signups > 0} />
              <Stat label="Signup conversion" value={`${ov.signup_conversion ?? 0}%`} />
              <Stat label="Sessions" value={ov.sessions ?? 0} />
              <Stat label="DAU" value={ov.dau ?? 0} />
              <Stat label="WAU" value={ov.wau ?? 0} />
              <Stat label="MAU" value={ov.mau ?? 0} />
              <Stat label="Avg session" value={fmtSec(ov.avg_session_seconds ?? 0)} />
            </div>
            {(ov.active_trend || []).length > 1 && (
              <div className="h-48 mt-5">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={ov.active_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="users" stroke={PURPLE} strokeWidth={2} dot={false} name="Active users" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          <Card title="Signup funnel & abandonment">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnelData} layout="vertical" margin={{ left: 40 }}>
                  <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v, n) => (n === "entered" ? [v, "Users reached"] : v)} />
                  <Bar dataKey="entered" radius={[0, 6, 6, 0]}>
                    {funnelData.map((_, i) => <Cell key={i} fill={PURPLE} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 space-y-1">
              {(d.fn?.stages || []).map((s) => (
                <div key={s.stage} className="flex justify-between text-xs text-slate-500">
                  <span>{s.label}</span>
                  <span>
                    {s.dropped_from_prev > 0 && <span className="text-red-600 mr-3">↓ {s.dropped_from_prev} ({s.drop_pct}%)</span>}
                    median {fmtMs(s.median_time_to_complete_ms)}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Top services used">
            {svcData.length === 0 ? <p className="text-slate-400 text-sm">No service activity yet.</p> : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={svcData} layout="vertical" margin={{ left: 40 }}>
                    <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="service" width={150} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="clicks" fill="#1565C0" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          <Card title="Screens (views · time on screen · exit rate)">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-400 text-xs border-b">
                    <th className="text-left py-2">Screen</th>
                    <th className="text-right">Views</th>
                    <th className="text-right">Users</th>
                    <th className="text-right">Time</th>
                    <th className="text-right">Exit%</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.sc?.screens || []).length === 0 ? (
                    <tr><td colSpan={5} className="text-slate-400 py-3">No screen views yet.</td></tr>
                  ) : (d.sc?.screens || []).slice(0, 25).map((s) => (
                    <tr key={s.screen} className="border-b border-slate-100">
                      <td className="py-2 text-slate-700 truncate max-w-[220px]">{s.screen}</td>
                      <td className="text-right">{s.views}</td>
                      <td className="text-right">{s.users}</td>
                      <td className="text-right">{fmtMs(s.median_time_on_screen_ms)}</td>
                      <td className={`text-right ${s.exit_rate > 60 ? "text-red-600" : ""}`}>{s.exit_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card title="Performance & abandonment signals">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Stat label="API median" value={fmtMs(pf.api_median_ms ?? 0)} />
              <Stat label="API p95" value={fmtMs(pf.api_p95_ms ?? 0)} danger={(pf.api_p95_ms ?? 0) > 3000} />
              <Stat label="API error rate" value={`${pf.api_error_rate ?? 0}%`} danger={(pf.api_error_rate ?? 0) > 2} />
              <Stat label="Crashes/errors" value={pf.crashes ?? 0} />
              <Stat label="Slow-session rate" value={`${pf.slow_session_rate ?? 0}%`} danger={(pf.slow_session_rate ?? 0) > 20} />
              <Stat label="API calls" value={pf.api_calls ?? 0} />
            </div>
            <div className="grid md:grid-cols-2 gap-6 mt-5">
              <div>
                <h3 className="text-sm font-bold text-slate-600 mb-2">Slowest endpoints</h3>
                {(pf.slowest_endpoints || []).length === 0 ? <p className="text-slate-400 text-sm">No API data yet.</p> :
                  (pf.slowest_endpoints || []).slice(0, 8).map((e) => (
                    <div key={e.endpoint} className="flex justify-between text-xs py-1 border-b border-slate-100">
                      <span className="truncate max-w-[200px] text-slate-700">{e.endpoint}</span>
                      <span className={e.median_ms > 3000 ? "text-red-600" : "text-slate-500"}>{fmtMs(e.median_ms)} · {e.count}x</span>
                    </div>
                  ))}
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-600 mb-2">Slowest screens to load</h3>
                {(pf.slowest_screens || []).length === 0 ? <p className="text-slate-400 text-sm">No load data yet.</p> :
                  (pf.slowest_screens || []).slice(0, 8).map((e) => (
                    <div key={e.screen} className="flex justify-between text-xs py-1 border-b border-slate-100">
                      <span className="truncate max-w-[200px] text-slate-700">{e.screen}</span>
                      <span className={e.median_ms > 2500 ? "text-red-600" : "text-slate-500"}>{fmtMs(e.median_ms)} · {e.count}x</span>
                    </div>
                  ))}
              </div>
            </div>
          </Card>

          <p className="text-xs text-slate-400 mt-2 mb-10">
            Installs = unique devices (store-install counts can be wired later). Slow-session rate flags
            sessions with a &gt;3s API call or an error — a likely cause of drop-off.
          </p>
        </div>
      )}
    </div>
  );
}
